import React, { useState, useEffect } from "react";
import { T, SUBJECTS, EXAM_SUBJECTS, HSC_SUBJECTS, VCE_SUBJECTS, PLANS, NAPLAN_CRITERIA, MIN_QUESTIONS_FOR_PCT, SPARKY_SERVER_URL } from '../shared/constants';
import { openBillingPortal } from '../../lib/billing';
import { Card, Badge, ProgressRing, ProgressBar } from '../shared/ui';
import ParentInsightReport from './ParentInsightReport';
import LumiInsightsTab from './LumiInsightsTab';
import WeeklyReport from './WeeklyReport';
import { learnSummaries } from '../../lib/topicProgress';

// The exam's subjects. NAPLAN-style exams use the shared subject list; HSC and VCE practice has its own course list, of which
// only the courses the child has practised are shown (the shared list knows just two of them, Biology and Chemistry)
function examSubjectList(prog, progress) {
  const courses = prog === 'hsc' ? HSC_SUBJECTS : prog === 'vce' ? VCE_SUBJECTS : null;
  if (courses) return courses.filter((s) => Object.keys(progress || {}).some((k) => k.startsWith(prog + '-') && k.endsWith('-' + s.id)));
  return EXAM_SUBJECTS[prog] ? SUBJECTS.filter((s) => EXAM_SUBJECTS[prog].includes(s.id)) : SUBJECTS;
}


export default function ParentDashboard({ progress, examProgram = "naplan", yearId = "y5", activeProfile = null, currentPlan = "plus", maxProfiles = 3, onClose, onUpgrade }) {
  const [tab, setTab] = useState(() => { const t = sessionStorage.getItem("lumio_dashboard_tab"); if(t){ sessionStorage.removeItem("lumio_dashboard_tab"); return t; } return "week"; });
  const [editingName, setEditingName] = useState(false);
  const [billing, setBilling] = useState(null); // null, "opening", or a message when the billing portal could not open
  const [nameInput, setNameInput] = useState(activeProfile?.name || "");
  // Learn progress (Years 6–12 topics): read from each topic's saved state, which syncs with the account.
  const [learn] = useState(() => learnSummaries(activeProfile?.id || '')); // this child's Learn progress

  // De-duplicate progress entries: scoped keys (exam-year-subject) take priority over
  // legacy bare subject keys so the total never double-counts (Bug 8 fix).
  // Scoped strictly to the active exam program - otherwise a parent viewing the
  // ACER/IGNITE dashboard sees totals bled in from an unrelated program (Bug: "stale
  // data from another product").
  const deduplicatedProgress = React.useMemo(() => {
    const entries = Object.entries(progress).filter(([k]) => {
      if (k.includes('-')) return k.startsWith(examProgram + '-');
      return (EXAM_SUBJECTS[examProgram] || []).includes(k);
    });
    const scopedKeys = new Set(entries.filter(([k]) => k.includes('-')).map(([k]) => k.split('-').pop()));
    return Object.fromEntries(entries.filter(([k]) => k.includes('-') || !scopedKeys.has(k)));
  }, [progress, examProgram]);
  const totalQ = Object.values(deduplicatedProgress).reduce((a, b) => a + (b.total || 0), 0);
  const totalCorrect = Object.values(deduplicatedProgress).reduce((a, b) => a + (b.correct || 0), 0);

  return (
    <div style={{ minHeight: "100%", background: T.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "20px 32px", borderBottom: `1px solid ${T.border}`, background: T.surface, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{"\u2190"}</button>
        <div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, color: T.text, margin: 0 }}>
                Parent dashboard
              </h2>
          <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>{({ exam: "Exam Only", premium: "Premium", family: "Family", essential: "Exam Only", plus: "Exam Only", selective: "Exam Only", allaccess: "Premium" })[currentPlan] || "Free Trial"} {"\u00B7"} Track progress & manage settings</p>
        </div>
      </div>

      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0 }}>
        {[{id:"week",label:"This week"},{id:"overview",label:"Overview"},{id:"subjects",label:"Subjects"},{id:"insights",label:"\u2728 Lumi"},{id:"settings",label:"Settings"}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "12px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: "transparent", color: tab === t.id ? T.accent : T.textMid, borderBottom: `2px solid ${tab === t.id ? T.accent : "transparent"}`, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "color .2s" }}>{t.label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {tab === "week" && <WeeklyReport activeProfile={activeProfile} maxProfiles={maxProfiles} />}

        {tab === "overview" && (
          <div style={{ animation: "fadeUp .3s ease", padding: "0 8px" }}>
            <ParentInsightReport progress={deduplicatedProgress} yearId={yearId} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <Card style={{ textAlign: "center", padding: 20 }}>
                {/* accuracy, once there are enough questions for it to mean something (the card beside it has the counts) */}
                <p style={{ fontSize: 32, fontFamily: "'Space Grotesk', sans-serif", color: T.accent, margin: 0 }}>{totalQ >= MIN_QUESTIONS_FOR_PCT ? `${Math.round((totalCorrect / totalQ) * 100)}%` : "\u2014"}</p>
                <p style={{ fontSize: 12, color: T.textMid, fontWeight: 600, marginTop: 6 }}>Accuracy</p>
                <p style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>{totalQ >= MIN_QUESTIONS_FOR_PCT ? `${totalCorrect} of ${totalQ} right` : `Shows after ${MIN_QUESTIONS_FOR_PCT} questions`}</p>
              </Card>
              <Card style={{ textAlign: "center", padding: 20 }}>
                <p style={{ fontSize: 32, fontFamily: "'Space Grotesk', sans-serif", color: T.text }}>{totalQ}</p>
                <p style={{ fontSize: 12, color: T.textMid, fontWeight: 600 }}>Questions Attempted</p>
                <p style={{ fontSize: 12, color: T.green, marginTop: 4 }}>{totalCorrect} correct</p>
              </Card>
            </div>
            {examSubjectList(examProgram, progress).map(s => {
              const allKeys = Object.keys(progress).filter(k => k.startsWith(examProgram + '-') && k.endsWith('-' + s.id));
              const p = allKeys.length > 0
                ? allKeys.reduce((acc, k) => ({ correct: acc.correct + (progress[k].correct||0), total: acc.total + (progress[k].total||0), topics: acc.topics }), { correct:0, total:0, topics:{} })
                : progress[s.id] || {};
              const pct = p.total > 0 ? Math.round((p.correct / p.total) * 100) : null;
              return (
                <Card key={s.id} style={{ marginBottom: 12, padding: "16px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>{s.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <p style={{ fontWeight: 600, fontSize: 14, color: T.text }}>{s.label}</p>
                        {pct !== null && <p style={{ fontSize: 13, color: s.color, fontWeight: 700 }}>{pct}%</p>}
                      </div>
                      {p.total > 0 && <ProgressBar value={p.correct || 0} max={Math.max(p.total, 1)} color={s.color} height={5} />}
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: T.textDim }}>{p.total || 0} questions attempted {"\u00B7"} {p.correct || 0} correct</p>
                </Card>
              );
            })}
            {learn.length > 0 && <p style={{ fontSize: 12, fontWeight: 700, color: T.textMid, margin: "18px 0 8px", letterSpacing: ".04em" }}>LEARN</p>}
            {learn.map(g => (
              <Card key={g.key} style={{ marginBottom: 12, padding: "16px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: T.text }}>{g.label}</p>
                  {g.points > 0 && <p style={{ fontSize: 13, color: T.accent, fontWeight: 700 }}>{g.pct}%</p>}
                </div>
                {g.points > 0 && <ProgressBar value={g.mastered} max={Math.max(g.points, 1)} color={T.accent} height={5} />}
                <p style={{ fontSize: 12, color: T.textDim, marginTop: 8 }}>{g.lessonsDone} lesson{g.lessonsDone === 1 ? "" : "s"} done {"\u00B7"} {g.mastered}/{g.points} syllabus points mastered{g.due > 0 ? ` \u00B7 ${g.due} review${g.due === 1 ? "" : "s"} due` : ""}</p>
              </Card>
            ))}
          </div>
        )}

        {tab === "subjects" && (
          <div style={{ animation: "fadeUp .3s ease" }}>
            {examSubjectList(examProgram, progress).map(s => {
              // Aggregate across ALL year levels for this subject/exam so mastery % is not gated by a yearId mismatch
              const allKeys2 = Object.keys(progress).filter(k => k.startsWith(examProgram + '-') && k.endsWith('-' + s.id));
              const p = allKeys2.length > 0
                ? allKeys2.reduce((acc, k) => ({ correct: acc.correct + (progress[k].correct||0), total: acc.total + (progress[k].total||0), topics: { ...acc.topics, ...progress[k].topics } }), { correct:0, total:0, topics:{} })
                : progress[s.id] || {};
              const topicData = p.topics || {};
              const proficiency = p.total > 0 ? Math.round((p.correct / p.total) * 100) : 0;
              // A subject with zero attempts is "not started," never "needs support" -
              // that badge should only ever reflect actual poor performance. p.total is
              // undefined (not 0) when a subject has no progress entry at all, so this
              // must be a falsy check, not a strict === 0 comparison.
              const notStarted = !p.total;
              const profLabel = notStarted ? "Not Started" : proficiency >= 80 ? "Exceeding" : proficiency >= 60 ? "Strong" : proficiency >= 40 ? "Developing" : "Needs Support";
              const profColor = notStarted ? T.textDim : proficiency >= 80 ? T.green : proficiency >= 60 ? T.accent : proficiency >= 40 ? T.gold : T.red;
              return (
                <Card key={s.id} style={{ marginBottom: 16, padding: "20px" }} glow={s.color}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <span style={{ fontSize: 28 }}>{s.icon}</span>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 2 }}>{s.label}</h3>
                      <Badge label={profLabel} color={profColor} />
                    </div>
                    <ProgressRing value={proficiency} max={100} size={50} color={s.color} label={`${proficiency}%`} />
                  </div>
                  {Object.keys(topicData).length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {Object.entries(topicData).map(([topic, td]) => {
                        const tp = td.total > 0 ? Math.round((td.correct / td.total) * 100) : null;
                        return (
                          <div key={topic}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                              <p style={{ fontSize: 12, color: T.textMid }}>{topic}</p>
                              <p style={{ fontSize: 12, color: tp !== null && tp >= 70 ? T.green : tp >= 50 ? T.gold : T.red, fontWeight: 600 }}>{tp !== null ? `${tp}%` : `${td.correct}/${td.total}`}</p>
                            </div>
                            <ProgressBar value={td.correct} max={Math.max(td.total, 1)} color={tp >= 70 ? T.green : tp >= 50 ? T.gold : T.red} height={4} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {Object.keys(topicData).length === 0 && (
                    <p style={{ fontSize: 13, color: T.textDim, textAlign: "center", padding: "8px 0" }}>No activity yet in this subject.</p>
                  )}
                </Card>
              );
            })}
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textMid, margin: "18px 0 8px", letterSpacing: ".04em" }}>LEARN</p>
            {learn.length === 0 && (
              <Card style={{ marginBottom: 16, padding: "20px" }}>
                <p style={{ fontSize: 13, color: T.textDim, textAlign: "center", margin: 0 }}>No Learn activity yet. Progress from Learn lessons will show here.</p>
              </Card>
            )}
            {learn.map(g => {
              // opened but nothing finished yet is "not started", never "needs support"
              const fresh = g.points === 0 || (g.lessonsDone === 0 && g.mastered === 0);
              const label = fresh ? "Not Started" : g.pct >= 80 ? "Exceeding" : g.pct >= 60 ? "Strong" : g.pct >= 40 ? "Developing" : "Needs Support";
              const color = fresh ? T.textDim : g.pct >= 80 ? T.green : g.pct >= 60 ? T.accent : g.pct >= 40 ? T.gold : T.red;
              return (
                <Card key={g.key} style={{ marginBottom: 16, padding: "20px" }} glow={T.accent}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 2 }}>{g.label}</h3>
                      <Badge label={label} color={color} />
                    </div>
                    <ProgressRing value={g.pct} max={100} size={50} color={T.accent} label={`${g.pct}%`} />
                  </div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                    <div><p style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: 0 }}>{g.lessonsDone}</p><p style={{ fontSize: 11, color: T.textMid, margin: 0 }}>lessons done</p></div>
                    <div><p style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: 0 }}>{g.mastered}/{g.points}</p><p style={{ fontSize: 11, color: T.textMid, margin: 0 }}>syllabus points mastered</p></div>
                    <div><p style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: 0 }}>{g.due}</p><p style={{ fontSize: 11, color: T.textMid, margin: 0 }}>reviews due</p></div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: g.focus.length ? 12 : 0 }}>
                    {g.topics.slice(0, 6).map(tp => (
                      <div key={tp.id}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                          <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>{tp.title}</p>
                          <p style={{ fontSize: 12, color: T.textMid, fontWeight: 600, margin: 0, whiteSpace: "nowrap" }}>{tp.mastered}/{tp.points}</p>
                        </div>
                        <ProgressBar value={tp.mastered} max={Math.max(tp.points, 1)} color={T.accent} height={4} />
                      </div>
                    ))}
                  </div>
                  {g.focus.length > 0 && (
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: T.textMid, marginBottom: 6 }}>Focus areas</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {g.focus.map(f => (
                          <a key={f.id} href={f.href} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: T.accent, textDecoration: "none" }}>
                            {"\u2192"} {f.label} <span style={{ color: T.textDim }}>({f.reason})</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {tab === "insights" && (
          <div style={{ animation: "fadeUp .3s ease" }}>
            <LumiInsightsTab progress={progress} serverUrl={SPARKY_SERVER_URL} activeProfile={activeProfile} examProgram={examProgram} />
          </div>
        )}

        {tab === "settings" && (
          <div style={{ animation: "fadeUp .3s ease" }}>
            <Card style={{ padding: "20px", marginBottom: 12 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: T.text, margin: "0 0 14px" }}>{"\uD83D\uDC64"} Profile</p>
              <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12 }}>
                <div style={{ width:52, height:52, borderRadius:"50%", background:`${T.accent}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>{activeProfile?.avatar || "\uD83C\uDF93"}</div>
                <div style={{ flex:1 }}>
                  {editingName ? (
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <input
                        value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        placeholder="Enter display name"
                        style={{ flex:1, border:`1px solid ${T.accent}`, borderRadius:8, padding:"6px 10px", fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", background:T.bg, color:T.text, outline:"none" }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            const trimmed = nameInput.trim();
                            if (trimmed) {
                              const profiles = JSON.parse(localStorage.getItem("lumio_profiles") || "[]");
                              const updated = profiles.map(p => p.id === activeProfile?.id ? { ...p, name: trimmed } : p);
                              localStorage.setItem("lumio_profiles", JSON.stringify(updated));
                            }
                            setEditingName(false);
                          }
                          if (e.key === "Escape") setEditingName(false);
                        }}
                        autoFocus
                      />
                      <button onClick={() => {
                        const trimmed = nameInput.trim();
                        if (trimmed) {
                          const profiles = JSON.parse(localStorage.getItem("lumio_profiles") || "[]");
                          const updated = profiles.map(p => p.id === activeProfile?.id ? { ...p, name: trimmed } : p);
                          localStorage.setItem("lumio_profiles", JSON.stringify(updated));
                        }
                        setEditingName(false);
                      }} style={{ background:T.accent, border:"none", borderRadius:8, color:"white", padding:"6px 12px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Save</button>
                    </div>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <p style={{ fontWeight:700, fontSize:15, color:T.text, margin:"0 0 2px" }}>{(activeProfile && activeProfile.name) ? activeProfile.name : "Student"}</p>
                      <button onClick={() => { setNameInput(activeProfile?.name || ""); setEditingName(true); }} style={{ background:"transparent", border:`1px solid ${T.border}`, borderRadius:6, padding:"2px 8px", fontSize:11, color:T.textMid, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Edit</button>
                    </div>
                  )}
                  <p style={{ fontSize:12, color:T.textMid, margin:"2px 0 0" }}>Year {String(activeProfile?.yearId || yearId || "").replace("y", "")} {"\u00B7"} {currentPlan === "trial" ? "Free Trial" : "Subscribed"}</p>
                </div>
              </div>
            </Card>
            <Card style={{ padding: "20px", marginBottom: 12 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: T.text, margin: "0 0 14px" }}>{"\uD83D\uDCCB"} Subscription</p>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <p style={{ fontWeight:600, fontSize:14, color:T.text, margin:"0 0 2px" }}>{({ exam: "Exam Only", premium: "Premium", family: "Family", essential: "Exam Only", plus: "Exam Only", selective: "Exam Only", allaccess: "Premium" })[currentPlan] || "Free Trial"}</p>
                  <p style={{ fontSize:12, color:T.textMid, margin:0 }}>{currentPlan === "trial" ? "7-day free trial: every exam, plus a sample of lessons" : currentPlan === "exam" || ["essential","plus","selective"].includes(currentPlan) ? "Every exam" : "Every exam and every lesson"}</p>
                </div>
                {currentPlan === "trial" && (
                  <button onClick={onUpgrade} style={{ background:T.accent, border:"none", borderRadius:10, color:"white", padding:"8px 16px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Upgrade {"\u2192"}</button>
                )}
                {currentPlan !== "trial" && (
                  <button onClick={async () => { setBilling("opening"); const msg = await openBillingPortal(); if (msg) setBilling(msg); }} disabled={billing === "opening"}
                    style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, padding:"8px 14px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", flex:"none" }}>{billing === "opening" ? "Opening…" : "Manage or cancel"}</button>
                )}
              </div>
              {currentPlan !== "trial" && <p style={{ fontSize:12, color: billing && billing !== "opening" ? "#B91C1C" : T.textMid, margin:"12px 0 0", lineHeight:1.5 }}>{billing && billing !== "opening" ? billing : "Switch plans, update your card, see invoices or cancel. If you cancel, your plan keeps working until the end of the period you have paid for."}</p>}
            </Card>
            {examSubjectList(examProgram, progress).some(s => NAPLAN_CRITERIA[s.id]) && (
            <Card style={{ padding: "20px" }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 8 }}>{"\uD83D\uDCCA"} Assessment Criteria</p>
              {examSubjectList(examProgram, progress).filter(s => NAPLAN_CRITERIA[s.id]).map(s => (
                <div key={s.id} style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: s.color, marginBottom: 6 }}>{s.icon} {s.label}</p>
                  {(NAPLAN_CRITERIA[s.id] || []).map((c, i) => (
                    <p key={i} style={{ fontSize: 12, color: T.textMid, marginBottom: 4, paddingLeft: 12, lineHeight: 1.6 }}>{"\u00B7"} {c}</p>
                  ))}
                </div>
              ))}
            </Card>
            )}
          </div>
        )}
      </div>
    </div>

  );
}
